import { getClaimForPropostaId } from './proposalClaimLookup';
import {
  allServicosHaveTerminalScd,
  anyServicoConcluidoNotCancelled,
  latestTerminalAtFromScdList,
} from './proposalComercialBoard';
import type { ProposalServiceFile } from './proposalServiceDetails';
import { serviceDisplayName } from './proposalServiceDetails';

export type LeadDetailRow = {
  key: string;
  label: string;
  value: string;
  href?: string;
};

export type RequestStatusTone = 'neutral' | 'progress' | 'success' | 'danger' | 'warning';

export type RequestStatusBadge = {
  label: string;
  tone: RequestStatusTone;
};

export type ServiceCompletionDisplay = {
  instanceId: string;
  title: string;
  statusLabel: string;
  tone: 'success' | 'danger' | 'warning';
  completedAt?: string;
  completedBy?: string;
  cancelledAt?: string;
  cancelledBy?: string;
  cancellationNotes?: string;
  resolvedByOther?: boolean;
  emailDataRows: LeadDetailRow[];
  files: ProposalServiceFile[];
};

export type RequestCancellationInfo = {
  cancelledAt: string | null;
  cancelledBy: string | null;
  feedback: string | null;
};

export type ComercialProposalRequestMeta = {
  id: string;
  propostaId: string;
  submittedAt: string | null;
  status: RequestStatusBadge;
  boardStatus: string;
  completionNotes: string | null;
  completedAt: string | null;
  completedBy: string | null;
  serviceCompletions: ServiceCompletionDisplay[];
  cancellation: RequestCancellationInfo | null;
  leadId: number | null;
  showCompletionBlock: boolean;
};

const KANBAN_STAGE_LABELS: Record<string, string> = {
  available: 'Por iniciar',
  roadtrip: 'RoadTrip',
  op_criada: 'OP criada',
  in_progress: 'Em processo',
  proposta_aprovacao: 'Proposta em aprovação',
  exclusividade_pendente: 'Exclusividade',
  pendente_gestor_edp: 'Pendente gestor EDP',
  pendente_comercial: 'Pendente comercial',
  completed: 'Concluído',
};

const firstNonEmpty = (...values: (unknown)[]): string | null => {
  for (const v of values) {
    if (v != null && String(v).trim()) return String(v).trim();
  }
  return null;
};

const formatDateTime = (iso?: string | null): string | null => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const formatDateOnly = (iso?: string | null): string | null => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('pt-PT');
  } catch {
    return iso;
  }
};

function proposalHasExplicitCccMarkers(proposal: Record<string, unknown>): boolean {
  if (proposal.submittedFromCcc === true) return true;
  const src = String(proposal.submittedBySource || proposal.submitted_by_source || '').toLowerCase();
  return src === 'ccc_recuperacao' || src === 'ccc_prospeccao' || src === 'ccc_retencao' || src === 'ccc';
}

/** Normalize legacy Prospeção rows synced without explicit CCC markers. */
export function normalizeCccProposalMarkers(
  proposal: Record<string, unknown>,
  marcacaoFormData?: Record<string, unknown>
): Record<string, unknown> {
  const fd = marcacaoFormData || {};
  const fromProspeccaoMarcacao =
    String(fd.cccSource || '').toLowerCase() === 'prospeccao' ||
    (fd.ccc_prospeccao_lead_id != null && String(fd.ccc_prospeccao_lead_id).trim());

  if (proposalHasExplicitCccMarkers(proposal) || fromProspeccaoMarcacao) {
    return {
      ...proposal,
      submittedFromCcc: true,
      submittedBySource:
        proposal.submittedBySource ||
        proposal.submitted_by_source ||
        (fromProspeccaoMarcacao ? 'ccc_prospeccao' : 'ccc'),
    };
  }
  return proposal;
}

export function isCccOriginatedProposal(
  proposal: Record<string, unknown>,
  marcacaoFormData?: Record<string, unknown>
): boolean {
  if (proposal.submittedFromCcc === true) return true;
  const src = String(proposal.submittedBySource || proposal.submitted_by_source || '').toLowerCase();
  if (src === 'ccc_recuperacao' || src === 'ccc_prospeccao' || src === 'ccc_retencao' || src === 'ccc') return true;
  const fd = marcacaoFormData || {};
  if (String(fd.cccSource || '').toLowerCase() === 'prospeccao') return true;
  if (fd.ccc_prospeccao_lead_id != null && String(fd.ccc_prospeccao_lead_id).trim()) return true;
  return false;
}

export function cccProposalSourceLabel(
  proposal: Record<string, unknown>,
  marcacaoFormData?: Record<string, unknown>
): string | null {
  const normalized = normalizeCccProposalMarkers(proposal, marcacaoFormData);
  if (!isCccOriginatedProposal(normalized, marcacaoFormData)) return null;
  const src = String(
    normalized.submittedBySource || normalized.submitted_by_source || ''
  ).toLowerCase();
  if (src === 'ccc_prospeccao') return 'CCC Prospeção';
  if (src === 'ccc_recuperacao') return 'CCC Recuperação';
  if (src === 'ccc_retencao') return 'CCC Reposição';
  if (String(marcacaoFormData?.cccSource || '').toLowerCase() === 'prospeccao') return 'CCC Prospeção';
  return 'CCC';
}

export type CompletedProposalForContract = {
  id: string;
  proposal: Record<string, unknown>;
  display: string;
  isCcc: boolean;
  cccSourceLabel: string | null;
  submittedAt: string | null;
};

type MarcacaoLikeForProposals = {
  id?: string | number;
  marcacao_id?: string;
  nome_empresa?: string;
  empresa?: string;
  form_data?: Record<string, unknown>;
};

/**
 * Completed BackOffice proposals for Submeter Contrato → Associar Proposta.
 * Uses the same completion rules as Minhas Propostas / Prospeção estado (SCD + claim status).
 */
export function buildCompletedProposalsForMarcacao(
  marcacao: MarcacaoLikeForProposals | undefined | null
): CompletedProposalForContract[] {
  if (!marcacao) return [];
  const formData = marcacao.form_data || {};
  const proposalsList = getAllProposalsFromMarcacao(formData).map((p) =>
    normalizeCccProposalMarkers(p, formData)
  );

  return proposalsList
    .map((proposal, idx) => {
      const propostaId = resolvePropostaId(proposal, formData, idx);
      const propostaIdStr = String(propostaId);
      const { boardStatus, claim } = resolveRequestBoardStatus(proposal, formData, propostaIdStr);
      if (boardStatus !== 'completed') return null;

      const serviceCompletionData = claim?.service_completion_data || [];
      const scdList = Array.isArray(serviceCompletionData) ? serviceCompletionData : [];
      const mergedServiceDetails = { ...(proposal?.serviceDetails || {}) } as Record<string, unknown>;
      scdList.forEach((scd: unknown) => {
        const row = (scd && typeof scd === 'object' ? scd : {}) as Record<string, unknown>;
        const instanceId = row.instanceId ?? row.instance_id;
        if (!instanceId) return;
        const emailData = (row.emailData || {}) as Record<string, unknown>;
        const existing = (mergedServiceDetails[String(instanceId)] || {}) as Record<string, unknown>;
        mergedServiceDetails[String(instanceId)] = {
          ...existing,
          idPropostaS2C: emailData.propostaId ?? emailData.idPropostaS2C ?? existing.idPropostaS2C,
          propostaId: emailData.propostaId ?? emailData.idPropostaS2C ?? existing.propostaId,
          valorProposta: emailData.investimento ?? existing.valorProposta,
          investimento: emailData.investimento ?? existing.investimento,
          idProposta: emailData.numeroProposta ?? emailData.idProposta ?? existing.idProposta,
          numeroProposta: emailData.numeroProposta ?? existing.numeroProposta,
          iban: emailData.iban ?? existing.iban,
          validade: emailData.validade ?? emailData.dataValidade ?? existing.validade,
          ...emailData,
        };
      });

      const mergedProposal = {
        ...proposal,
        serviceDetails: mergedServiceDetails,
      };
      const empresa = (mergedProposal.empresa || {}) as Record<string, unknown>;
      const companyName =
        empresa.nomeEmpresa || marcacao.nome_empresa || marcacao.empresa || 'Sem nome';
      const nif = empresa.nifEmpresa || '';
      const cccLabel = cccProposalSourceLabel(mergedProposal, formData);
      const isCcc = cccLabel != null;
      const display = nif
        ? `${propostaIdStr} – ${companyName} (NIF: ${nif})`
        : `${propostaIdStr} – ${companyName}`;

      return {
        id: propostaIdStr,
        proposal: mergedProposal,
        display,
        isCcc,
        cccSourceLabel: cccLabel,
        submittedAt: firstNonEmpty(proposal.submittedAt, proposal.submitted_at),
      };
    })
    .filter((p): p is CompletedProposalForContract => p !== null);
}

export function getAllProposalsFromMarcacao(
  fd: Record<string, unknown> | undefined
): Record<string, unknown>[] {
  if (!fd) return [];
  const raw = fd.proposalData;
  let list: Record<string, unknown>[] = [];
  if (Array.isArray(raw)) {
    list = raw.filter((p) => p && typeof p === 'object' && !Array.isArray(p)) as Record<string, unknown>[];
  } else if (raw && typeof raw === 'object') {
    list = [raw as Record<string, unknown>];
  }
  if (!list.length) return [];
  return [...list].sort((a, b) => {
    const ta = String(a.submittedAt || a.submitted_at || '');
    const tb = String(b.submittedAt || b.submitted_at || '');
    return tb.localeCompare(ta);
  });
}

function getPropostaIdsArray(fd: Record<string, unknown>): string[] {
  const raw = fd.propostaId ?? fd.proposta_id;
  if (Array.isArray(raw)) return raw.map(String);
  if (raw != null && String(raw).trim()) return [String(raw).trim()];
  return [];
}

function resolvePropostaId(
  proposal: Record<string, unknown>,
  fd: Record<string, unknown>,
  index: number
): string {
  const ids = getPropostaIdsArray(fd);
  const fromProposal = firstNonEmpty(proposal.propostaId, proposal.proposta_id);
  // Prefer the id stored on the proposal — proposalData is sorted by date but propostaId[] is not.
  const fromList = ids[index] || ids[0];
  const raw = fromProposal || fromList || `pedido-${index + 1}`;
  return raw.startsWith('PRP-') ? raw : `PRP-${raw}`;
}

function isRoadtripProposal(proposal: Record<string, unknown>): boolean {
  const ei = proposal.equipaInterna as Record<string, unknown> | undefined;
  return ei?.tipoPedido === 'roadtrip';
}

export function resolveRequestBoardStatus(
  proposal: Record<string, unknown>,
  fd: Record<string, unknown>,
  propostaId: string
): { boardStatus: string; claim: Record<string, unknown> | null } {
  const claimedRaw = fd.claimedProposals;
  const claimedMap =
    typeof claimedRaw === 'object' && claimedRaw !== null && !Array.isArray(claimedRaw)
      ? (claimedRaw as Record<string, unknown>)
      : undefined;
  const claim = getClaimForPropostaId(claimedMap, propostaId);
  const servicos = Array.isArray(proposal.servicos) ? proposal.servicos : [];
  const scdRaw = claim?.service_completion_data;
  const scdList = Array.isArray(scdRaw) ? scdRaw : [];
  const claimStatus = String(claim?.status || '').toLowerCase();

  let boardStatus = 'available';
  if (claimStatus === 'cancelled') {
    boardStatus = 'cancelled';
  } else if (
    servicos.length > 0 &&
    allServicosHaveTerminalScd(servicos, scdList) &&
    !anyServicoConcluidoNotCancelled(servicos, scdList)
  ) {
    boardStatus = 'cancelled';
  } else if (
    claimStatus === 'completed' ||
    (servicos.length > 0 &&
      allServicosHaveTerminalScd(servicos, scdList) &&
      anyServicoConcluidoNotCancelled(servicos, scdList))
  ) {
    boardStatus = 'completed';
  } else if (
    servicos.length === 0 &&
    scdList.length > 0 &&
    claimStatus === 'completed'
  ) {
    boardStatus = 'completed';
  } else if (claim?.backoffice_user_id || claim?.claimed_by) {
    boardStatus = 'in_progress';
  }

  return { boardStatus, claim };
}

function buildStatusBadge(
  boardStatus: string,
  claim: Record<string, unknown> | null,
  proposal: Record<string, unknown>
): RequestStatusBadge {
  if (boardStatus === 'cancelled') {
    return { label: 'Cancelado', tone: 'danger' };
  }
  if (boardStatus === 'completed') {
    return { label: 'Concluído', tone: 'success' };
  }
  if (boardStatus === 'in_progress') {
    const stage = String(claim?.board_stage || '').toLowerCase().trim();
    if (stage && KANBAN_STAGE_LABELS[stage]) {
      return { label: KANBAN_STAGE_LABELS[stage], tone: 'progress' };
    }
    return { label: 'Em processo', tone: 'progress' };
  }
  if (isRoadtripProposal(proposal)) {
    return { label: 'RoadTrip', tone: 'warning' };
  }
  return { label: 'Por iniciar', tone: 'neutral' };
}

function normalizeCompletionFiles(files: unknown, leadId: number | null): ProposalServiceFile[] {
  if (!Array.isArray(files)) return [];
  return files
    .filter((f) => f && typeof f === 'object')
    .map((f) => {
      const file = f as Record<string, unknown>;
      return {
        name: String(file.name || file.file_name || file.fileName || 'ficheiro'),
        size: file.size as number | string | undefined,
        type: String(file.type || file.file_type || file.content_type || ''),
        lead_file_id: (file.lead_file_id || file.leadFileId || file.file_id || file.id) as
          | number
          | string
          | undefined,
        lead_id: (file.lead_id || file.leadId || leadId) as number | string | undefined,
        public_id: String(file.public_id || file.publicId || file.cloudinary_id || '') || undefined,
        url: String(file.url || file.file_url || file.download_url || '') || undefined,
        data: String(file.data || file.file_data || '') || undefined,
        field: String(file.field || 'documento'),
      };
    });
}

function buildEmailDataRows(emailData: Record<string, unknown>): LeadDetailRow[] {
  const rows: LeadDetailRow[] = [];
  const push = (key: string, label: string, value: unknown) => {
    if (value == null || value === '') return;
    rows.push({ key, label, value: String(value) });
  };
  push('validade', 'Validade', formatDateOnly(String(emailData.validade || emailData.dataValidade || '')) || emailData.validade);
  push('propostaId', 'ID da proposta (S2C)', emailData.propostaId || emailData.idPropostaS2C);
  push('investimento', 'Investimento', emailData.investimento != null ? `${emailData.investimento} €` : null);
  push('numeroProposta', 'Nº proposta', emailData.numeroProposta);
  push('dataValidade', 'Data validade', formatDateOnly(String(emailData.dataValidade || '')));
  push('dataEnvio', 'Data de envio', formatDateOnly(String(emailData.dataEnvio || '')));
  push('horaEnvio', 'Hora de envio', emailData.horaEnvio);
  push('iban', 'IBAN', emailData.iban);
  return rows;
}

function buildServiceCompletions(
  proposal: Record<string, unknown>,
  claim: Record<string, unknown> | null,
  leadId: number | null
): ServiceCompletionDisplay[] {
  const servicos = Array.isArray(proposal.servicos) ? proposal.servicos : [];
  const scdRaw = claim?.service_completion_data;
  const scdList = Array.isArray(scdRaw) ? scdRaw : [];
  if (!scdList.length) return [];

  return scdList.map((scd: unknown, index: number) => {
    const row = (scd && typeof scd === 'object' ? scd : {}) as Record<string, unknown>;
    const instanceId = String(row.instanceId ?? row.instance_id ?? `scd-${index}`);
    const serviceData = servicos.find((s: unknown) => {
      if (!s || typeof s !== 'object') return false;
      const so = s as Record<string, unknown>;
      const iid = String(so.instanceId ?? so.instance_id ?? '');
      return iid === instanceId;
    }) as Record<string, unknown> | undefined;
    const serviceType = String(row.service || serviceData?.service || 'servico');
    const instanceNumber = Number(serviceData?.instanceNumber) || index + 1;
    const isCancelled =
      !!row.cancelled_at || String(row.status || '').toLowerCase() === 'cancelled';
    const isCompleted =
      !isCancelled &&
      (!!row.completed_at || String(row.status || '').toLowerCase() === 'completed');
    const emailData = (row.emailData || {}) as Record<string, unknown>;

    let statusLabel = 'Resposta BackOffice';
    let tone: ServiceCompletionDisplay['tone'] = 'warning';
    if (isCancelled) {
      statusLabel = 'Cancelado (isolado)';
      tone = 'danger';
    } else if (isCompleted) {
      statusLabel = 'Concluído';
      tone = 'success';
    } else if (row.resolved_by_other) {
      statusLabel = 'Resolvido por outro serviço';
      tone = 'success';
    }

    return {
      instanceId,
      title: `Serviço ${instanceNumber}: ${serviceDisplayName(serviceType)}`,
      statusLabel,
      tone,
      completedAt: formatDateTime(String(row.completed_at || '')) || undefined,
      completedBy: firstNonEmpty(row.completed_by_username, row.completed_by_name) || undefined,
      cancelledAt: formatDateTime(String(row.cancelled_at || '')) || undefined,
      cancelledBy: firstNonEmpty(row.cancelled_by_username, row.cancelled_by_name) || undefined,
      cancellationNotes: firstNonEmpty(row.cancellation_notes, row.cancellation_feedback) || undefined,
      resolvedByOther: !!row.resolved_by_other,
      emailDataRows: buildEmailDataRows(emailData),
      files: normalizeCompletionFiles(row.files, leadId),
    };
  });
}

export function getCccProposalsFromMarcacao(
  fd: Record<string, unknown> | undefined
): Record<string, unknown>[] {
  return getAllProposalsFromMarcacao(fd).filter((p) => isCccOriginatedProposal(p, fd));
}

export function hasCccPedidoProposta(fd: Record<string, unknown> | undefined): boolean {
  return getCccProposalsFromMarcacao(fd).length > 0;
}

const TERMINAL_PROPOSAL_BOARD_STATUSES = new Set(['completed', 'cancelled']);

export type LeadProposalStatusDisplay = {
  label: string;
  tone: RequestStatusTone;
};

function sortRequestMetasByNewest(
  metas: ComercialProposalRequestMeta[]
): ComercialProposalRequestMeta[] {
  return [...metas].sort((a, b) => {
    const ta = a.submittedAt ? new Date(a.submittedAt).getTime() : 0;
    const tb = b.submittedAt ? new Date(b.submittedAt).getTime() : 0;
    if (tb !== ta) return tb - ta;
    return String(b.propostaId).localeCompare(String(a.propostaId));
  });
}

/**
 * Single label for Prospeção table "Estado da Proposta":
 * no proposals → "Nenhum pedido feito"; otherwise prefer any non-terminal request status.
 */
export function resolveLeadProposalStatusDisplay(
  fd: Record<string, unknown> | undefined
): LeadProposalStatusDisplay {
  const proposals = getAllProposalsFromMarcacao(fd);
  if (!proposals.length) {
    return { label: 'Nenhum pedido feito', tone: 'neutral' };
  }

  const metas = proposals.map((proposal, index) =>
    buildComercialRequestMeta(fd || {}, proposal, index)
  );
  const active = metas.filter((m) => !TERMINAL_PROPOSAL_BOARD_STATUSES.has(m.boardStatus));
  const pick = sortRequestMetasByNewest(active.length > 0 ? active : metas)[0];
  return { label: pick.status.label, tone: pick.status.tone };
}

export function buildComercialRequestMeta(
  fd: Record<string, unknown>,
  proposal: Record<string, unknown>,
  index: number
): ComercialProposalRequestMeta {
  const propostaId = resolvePropostaId(proposal, fd, index);
  const { boardStatus, claim } = resolveRequestBoardStatus(proposal, fd, propostaId);
  const status = buildStatusBadge(boardStatus, claim, proposal);
  const submittedAt = firstNonEmpty(proposal.submittedAt, proposal.submitted_at);
  const proposalFd: Record<string, unknown> = {
    servicos: proposal.servicos,
    serviceDetails: proposal.serviceDetails,
    fileReferences: proposal.fileReferences,
    empresa: proposal.empresa,
    equipaInterna: proposal.equipaInterna ?? fd.equipaInterna,
    lead_id: proposal.lead_id ?? proposal.leadId ?? fd.lead_id ?? fd.leadId,
  };
  const leadId = (() => {
    const candidates = [proposalFd.lead_id, proposalFd.leadId];
    for (const c of candidates) {
      const n = Number(c);
      if (Number.isFinite(n) && n > 0) return n;
    }
    return null;
  })();
  const scdList = Array.isArray(claim?.service_completion_data) ? claim.service_completion_data : [];
  const serviceCompletions = buildServiceCompletions(proposal, claim, leadId);

  const completedAt =
    formatDateTime(
      firstNonEmpty(claim?.completed_at, latestTerminalAtFromScdList(scdList))
    ) || null;
  const completedBy = firstNonEmpty(
    claim?.completed_by_username,
    claim?.backoffice_user_name
  );

  const cancellation: RequestCancellationInfo | null =
    boardStatus === 'cancelled'
      ? {
          cancelledAt:
            formatDateTime(firstNonEmpty(claim?.cancelled_at, latestTerminalAtFromScdList(scdList))) ||
            null,
          cancelledBy: firstNonEmpty(claim?.cancelled_by_username, claim?.cancelled_by_name),
          feedback: firstNonEmpty(claim?.cancellation_feedback, claim?.cancellation_notes),
        }
      : null;

  const completionNotes = firstNonEmpty(claim?.completion_notes);

  return {
    id: `request-${propostaId}-${index}`,
    propostaId,
    submittedAt,
    status,
    boardStatus,
    completionNotes,
    completedAt,
    completedBy,
    serviceCompletions,
    cancellation,
    leadId,
    showCompletionBlock: serviceCompletions.length > 0,
  };
}
